import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Menu } from '@headlessui/react';
import {
  Database,
  Zap,
  Sparkles,
  Users,
  ChevronDown,
  MoreHorizontal,
  Settings,
  RefreshCw,
  Link2,
  Trash2,
  Table2,
  BookOpen,
  Clock,
  Activity,
  ArrowRight,
  PauseCircle,
  Power,
  CreditCard,
  SlidersHorizontal,
} from 'lucide-react';
import { clsx } from 'clsx';
import { useTimezone } from '../../contexts/TimezoneContext';
import type { OverviewSource } from '../../services/sources-api';
import {
  integrationDisplay,
  syncDisplay,
  needsAttention,
  attentionDisplay,
  deactivationCopy,
  setupKind,
} from './sourceDisplay';
import Toggle from './Toggle';

export type DrawerTab = 'general' | 'setup' | 'accounts' | 'access' | 'danger';

interface SourceRowProps {
  source: OverviewSource;
  isAdmin: boolean;
  /** False when the workspace has no free source slot — reactivating then needs
   *  a plan change, so the CTA sends the user to billing instead. */
  hasSourceCapacity: boolean;
  reactivating: boolean;
  syncing: boolean;
  onOpenDrawer: (source: OverviewSource, tab: DrawerTab) => void;
  onToggle: (source: OverviewSource, field: 'syncEnabled' | 'aiAccessEnabled', value: boolean) => void;
  onSyncNow: (source: OverviewSource) => void;
  onReconnect: (source: OverviewSource) => void;
  onReactivate: (source: OverviewSource) => void;
}

const ROW_GRID =
  'grid grid-cols-[minmax(0,1fr)_auto] lg:grid-cols-[minmax(0,2.4fr)_165px_110px_90px_80px_70px] gap-3 items-center';

const SourceRow: React.FC<SourceRowProps> = ({
  source,
  isAdmin,
  hasSourceCapacity,
  reactivating,
  syncing,
  onOpenDrawer,
  onToggle,
  onSyncNow,
  onReconnect,
  onReactivate,
}) => {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const { formatRelativeTime } = useTimezone();

  const display = integrationDisplay(source);
  const sync = syncDisplay(source, formatRelativeTime);
  const attention = needsAttention(source) ? attentionDisplay(source) : null;
  const isIngestion = source.mode === 'ingestion';
  const isWebhook = source.sourceType === 'webhook';
  const hasSetup = setupKind(source.sourceType) !== null;
  const firstAccountId = source.accounts[0]?.id;
  const isOff = !source.isActive;
  // Update mode only exists for Plaid, and only helps once the Item is broken.
  const canReconnect =
    source.sourceType === 'plaid' &&
    !isOff &&
    ['auth_required', 'error', 'disconnected'].includes(source.healthStatus);

  const stop = (e: React.MouseEvent): void => e.stopPropagation();

  return (
    <div className={clsx('border-t border-border', isOff && 'bg-muted/30')}>
      {/* Row */}
      <div
        className={clsx(ROW_GRID, 'px-4 py-3 cursor-pointer hover:bg-muted/50')}
        onClick={() => setOpen((o) => !o)}
      >
        {/* Source */}
        <div className="flex items-center gap-3 min-w-0">
          <div
            className={clsx(
              'w-9 h-9 rounded-[10px] flex-none overflow-hidden flex items-center justify-center bg-muted',
              isOff && 'grayscale opacity-50',
            )}
          >
            {display.logo ? (
              <img src={display.logo} alt="" className="w-9 h-9 object-contain" />
            ) : (
              <Database className="w-4 h-4 text-muted-foreground" />
            )}
          </div>
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <span
                className={clsx(
                  'text-sm font-semibold truncate',
                  isOff ? 'text-muted-foreground' : 'text-foreground',
                )}
              >
                {source.name}
              </span>
              {isOff && (
                <span className="inline-flex items-center gap-1 flex-none text-[11px] font-semibold rounded-full px-2 py-0.5 bg-warn/15 text-warn">
                  <PauseCircle className="w-3 h-3" /> Off · plan limit
                </span>
              )}
            </div>
            <div className="flex items-center gap-2 mt-0.5">
              {isIngestion ? (
                <span
                  className={clsx(
                    'inline-flex items-center gap-1 text-[11px] font-semibold rounded-full px-2 py-0.5',
                    isOff ? 'bg-muted text-muted-foreground' : 'bg-primary/15 text-primary',
                  )}
                >
                  <Database className="w-3 h-3" /> Stored copy
                </span>
              ) : (
                <span
                  className={clsx(
                    'inline-flex items-center gap-1 text-[11px] font-semibold rounded-full px-2 py-0.5',
                    isOff ? 'bg-muted text-muted-foreground' : 'bg-info/15 text-info',
                  )}
                >
                  <Zap className="w-3 h-3" /> On-demand
                </span>
              )}
              <span className="text-[11.5px] text-muted-foreground truncate">
                {display.name}
                {source.accounts.length > 1 && ` · ${source.accounts.length} accounts`}
                {source.mode === 'federated' && ' · your database'}
              </span>
            </div>
          </div>
        </div>

        {/* Sync status */}
        <div className="hidden lg:flex items-center gap-2 text-[12.5px]">
          <span
            className={clsx(
              'w-2 h-2 rounded-full flex-none',
              sync.dot === 'ok' && 'bg-success',
              sync.dot === 'warn' && 'bg-warn',
              sync.dot === 'off' && 'bg-muted-foreground',
            )}
          />
          <span className={sync.isProblem ? 'text-warn font-semibold' : 'text-muted-foreground'}>
            {sync.text}
          </span>
        </div>

        {/* AI access — a switched-off source is unreadable regardless of its own
            aiAccessEnabled flag (the backend gates on is_active first). */}
        <div className="hidden lg:block">
          {isOff ? (
            <span className="inline-flex items-center gap-1 text-[11.5px] font-semibold rounded-full px-2.5 py-0.5 bg-muted text-muted-foreground">
              No access
            </span>
          ) : source.aiAccessEnabled ? (
            <span className="inline-flex items-center gap-1 text-[11.5px] font-semibold rounded-full px-2.5 py-0.5 bg-success/15 text-success">
              <Sparkles className="w-3 h-3" /> On
            </span>
          ) : (
            <span className="inline-flex items-center gap-1 text-[11.5px] font-semibold rounded-full px-2.5 py-0.5 bg-muted text-muted-foreground">
              Paused
            </span>
          )}
        </div>

        {/* Queries 30d — deep link into the Query log */}
        <button
          className="hidden lg:block text-left text-[13.5px] font-semibold tabular-nums text-foreground hover:text-primary hover:underline"
          onClick={(e) => {
            stop(e);
            navigate(`/query-log?source=${source.id}`);
          }}
        >
          {source.counts.queries30d.toLocaleString()}
        </button>

        {/* People (admin-only count — D34) */}
        <div className="hidden lg:flex items-center gap-1.5 text-[13px] text-muted-foreground">
          <Users className="w-3.5 h-3.5" />
          {source.counts.people ?? '—'}
        </div>

        {/* Actions */}
        <div className="flex items-center gap-0.5 justify-end">
          {isAdmin && (
            <Menu as="div" onClick={stop}>
              <Menu.Button className="w-7 h-7 rounded-lg flex items-center justify-center text-muted-foreground hover:bg-muted hover:text-foreground">
                <MoreHorizontal className="w-4 h-4" />
              </Menu.Button>
              {/* anchor portals the menu out of the row card's overflow-hidden, and flips it above the button near the viewport bottom */}
              <Menu.Items
                anchor={{ to: 'bottom end', gap: 4 }}
                className="z-30 min-w-[190px] bg-card border border-border rounded-[10px] shadow-lg p-1 focus:outline-none"
              >
                {isOff && (
                  <Menu.Item>
                    {({ active }) => (
                      <button
                        disabled={reactivating}
                        className={clsx(
                          'flex items-center gap-2 w-full px-2.5 py-1.5 rounded-md text-[13px] text-left text-foreground disabled:opacity-50',
                          active && 'bg-muted',
                        )}
                        onClick={() => onReactivate(source)}
                      >
                        <Power className="w-3.5 h-3.5" />
                        {hasSourceCapacity ? 'Turn back on' : 'Turn back on — needs plan change'}
                      </button>
                    )}
                  </Menu.Item>
                )}
                <Menu.Item>
                  {({ active }) => (
                    <button
                      className={clsx(
                        'flex items-center gap-2 w-full px-2.5 py-1.5 rounded-md text-[13px] text-left text-foreground',
                        active && 'bg-muted',
                      )}
                      onClick={() => onOpenDrawer(source, 'general')}
                    >
                      <Settings className="w-3.5 h-3.5" /> Source settings
                    </button>
                  )}
                </Menu.Item>
                {hasSetup && (
                  <Menu.Item>
                    {({ active }) => (
                      <button
                        className={clsx(
                          'flex items-center gap-2 w-full px-2.5 py-1.5 rounded-md text-[13px] text-left text-foreground',
                          active && 'bg-muted',
                        )}
                        onClick={() => onOpenDrawer(source, 'setup')}
                      >
                        <SlidersHorizontal className="w-3.5 h-3.5" /> Setup
                      </button>
                    )}
                  </Menu.Item>
                )}
                <Menu.Item>
                  {({ active }) => (
                    <button
                      className={clsx(
                        'flex items-center gap-2 w-full px-2.5 py-1.5 rounded-md text-[13px] text-left text-foreground',
                        active && 'bg-muted',
                      )}
                      onClick={() => onOpenDrawer(source, 'access')}
                    >
                      <Users className="w-3.5 h-3.5" /> Manage access
                    </button>
                  )}
                </Menu.Item>
                {isIngestion && !isWebhook && !isOff && (
                  <Menu.Item>
                    {({ active }) => (
                      <button
                        className={clsx(
                          'flex items-center gap-2 w-full px-2.5 py-1.5 rounded-md text-[13px] text-left text-foreground',
                          active && 'bg-muted',
                        )}
                        onClick={() => onSyncNow(source)}
                      >
                        <RefreshCw className="w-3.5 h-3.5" /> Sync now
                      </button>
                    )}
                  </Menu.Item>
                )}
                {canReconnect && (
                  <Menu.Item>
                    {({ active }) => (
                      <button
                        className={clsx(
                          'flex items-center gap-2 w-full px-2.5 py-1.5 rounded-md text-[13px] text-left text-foreground',
                          active && 'bg-muted',
                        )}
                        onClick={() => onReconnect(source)}
                      >
                        <Link2 className="w-3.5 h-3.5" /> Reconnect bank
                      </button>
                    )}
                  </Menu.Item>
                )}
                <div className="h-px bg-border my-1 mx-1" />
                <Menu.Item>
                  {({ active }) => (
                    <button
                      className={clsx(
                        'flex items-center gap-2 w-full px-2.5 py-1.5 rounded-md text-[13px] text-left text-destructive',
                        active && 'bg-muted',
                      )}
                      onClick={() => onOpenDrawer(source, 'danger')}
                    >
                      <Trash2 className="w-3.5 h-3.5" /> Delete source…
                    </button>
                  )}
                </Menu.Item>
              </Menu.Items>
            </Menu>
          )}
          <span className="w-7 h-7 rounded-lg flex items-center justify-center text-muted-foreground">
            <ChevronDown className={clsx('w-4 h-4 transition-transform', open && 'rotate-180')} />
          </span>
        </div>
      </div>

      {/* Expanded panel */}
      {open && (
        <div className="border-t border-dashed border-border bg-muted/40 px-4 py-4 lg:pl-16">
          {isOff && (
            <div className="flex items-center gap-2.5 rounded-lg border border-warn/25 bg-warn/10 px-3 py-2.5 mb-3.5 text-[12.5px] text-foreground">
              <PauseCircle className="w-4 h-4 text-warn flex-none" />
              <span>{deactivationCopy(source)}</span>
              {isAdmin && (
                <button
                  disabled={reactivating}
                  className="ml-auto flex-none inline-flex items-center gap-1.5 rounded-[10px] bg-primary px-3 py-1.5 text-[13px] font-medium text-primary-foreground hover:opacity-90 disabled:opacity-50"
                  onClick={() => onReactivate(source)}
                >
                  {hasSourceCapacity ? (
                    <>
                      <Power className="w-3.5 h-3.5" />
                      {reactivating ? 'Turning on…' : 'Turn back on'}
                    </>
                  ) : (
                    <>
                      <CreditCard className="w-3.5 h-3.5" /> Change plan to turn on
                    </>
                  )}
                </button>
              )}
            </div>
          )}

          {attention && (
            <div className="flex items-center gap-2.5 rounded-lg border border-warn/25 bg-warn/10 px-3 py-2.5 mb-3.5 text-[12.5px] text-foreground">
              <Activity className="w-4 h-4 text-warn flex-none" />
              <span>{attention.message}</span>
              {isAdmin &&
                (attention.action === 'retry' ? (
                  <button
                    className="ml-auto flex-none inline-flex items-center gap-1.5 rounded-[10px] border border-input bg-card px-3 py-1.5 text-[13px] font-medium text-foreground hover:bg-accent disabled:opacity-50"
                    disabled={syncing}
                    onClick={() => onSyncNow(source)}
                  >
                    <RefreshCw className={clsx('w-3.5 h-3.5', syncing && 'animate-spin')} />
                    {syncing ? 'Retrying…' : 'Retry now'}
                  </button>
                ) : (
                  <button
                    className="ml-auto flex-none inline-flex items-center gap-1.5 rounded-[10px] border border-input bg-card px-3 py-1.5 text-[13px] font-medium text-foreground hover:bg-accent"
                    onClick={() => navigate('/sources/integrations')}
                  >
                    Reconnect
                  </button>
                ))}
            </div>
          )}

          {source.accounts.length > 1 && (
            <div className="flex flex-col gap-1.5 mb-3.5">
              {source.accounts.map((a) => (
                <div
                  key={a.id}
                  className="flex items-center gap-2.5 rounded-lg border border-border bg-card px-3 py-2 text-[12.5px]"
                >
                  <span className="font-semibold text-foreground">{a.name ?? 'Account'}</span>
                  <span className="text-muted-foreground">{a.type}</span>
                  {isIngestion && (
                    <span className="ml-auto font-semibold tabular-nums text-success">
                      +{a.recordsToday} today
                    </span>
                  )}
                </div>
              ))}
            </div>
          )}

          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-3.5 max-w-xl">
            <PanelStat
              label="New today"
              value={isIngestion ? `+${source.counts.recordsToday}` : '—'}
              tone={isIngestion && source.counts.recordsToday > 0 ? 'success' : 'muted'}
            />
            <PanelStat label="Semantics" value={String(source.counts.semantics)} />
            <PanelStat label="Routines" value={String(source.counts.routines)} />
            <PanelStat
              label="Pulse"
              value={!isIngestion ? '—' : source.pulse === 'active' ? 'Active' : source.pulse === 'eligible' ? 'Eligible' : '—'}
              tone={source.pulse === 'active' && isIngestion ? 'info' : 'muted'}
            />
          </div>

          <div className="flex flex-wrap items-center gap-2">
            {isIngestion && (
              <QuickLink
                icon={<Table2 className="w-3.5 h-3.5" />}
                disabled={isOff}
                title={isOff ? 'Turn this source back on to browse its data' : undefined}
                onClick={() => navigate(`/data-viewer?account=${firstAccountId ?? ''}`)}
              >
                View data
              </QuickLink>
            )}
            <QuickLink
              icon={<BookOpen className="w-3.5 h-3.5" />}
              disabled={!source.wiki.pageId}
              title={source.wiki.pageId ? undefined : 'No wiki page yet for this source'}
              onClick={() => navigate(`/wiki/${source.wiki.pageId}`)}
            >
              Wiki
            </QuickLink>
            <QuickLink
              icon={<Sparkles className="w-3.5 h-3.5" />}
              onClick={() => navigate(`/semantics?source=${source.id}`)}
            >
              Semantics <span className="text-muted-foreground font-semibold">{source.counts.semantics}</span>
            </QuickLink>
            <QuickLink icon={<Clock className="w-3.5 h-3.5" />} onClick={() => navigate('/routines')}>
              Routines <span className="text-muted-foreground font-semibold">{source.counts.routines}</span>
            </QuickLink>
            {isAdmin && (
              <QuickLink
                icon={<Settings className="w-3.5 h-3.5" />}
                onClick={() => onOpenDrawer(source, 'general')}
              >
                Settings
              </QuickLink>
            )}
            {/* Ingestion-only: on-demand sources store nothing, so there is no
                history for the overview to draw. */}
            {isIngestion && (
              <button
                className="ml-auto inline-flex items-center gap-1 text-[12.5px] font-semibold text-primary hover:underline"
                onClick={() => navigate(`/sources/${source.id}`)}
              >
                Open source page <ArrowRight className="w-3.5 h-3.5" />
              </button>
            )}
          </div>

          <div className="flex items-center gap-5 mt-3.5 pt-3 border-t border-dashed border-border">
            {isIngestion && (
              <label className="flex items-center gap-2 text-[12.5px] text-muted-foreground">
                <Toggle
                  checked={source.syncEnabled && !isOff}
                  disabled={!isAdmin || isOff}
                  label={isWebhook ? 'Receiving' : 'Syncing'}
                  onChange={(v) => onToggle(source, 'syncEnabled', v)}
                />
                {isWebhook ? 'Receiving' : 'Syncing'}
              </label>
            )}
            <label className="flex items-center gap-2 text-[12.5px] text-muted-foreground">
              <Toggle
                checked={source.aiAccessEnabled && !isOff}
                disabled={!isAdmin || isOff}
                label="AI access"
                onChange={(v) => onToggle(source, 'aiAccessEnabled', v)}
              />
              AI access
            </label>
            {isOff && (
              <span className="ml-auto text-[11.5px] text-muted-foreground">
                Nothing syncs and no AI can read this source while it&apos;s off
              </span>
            )}
            {!isOff && source.nextSyncAt && source.syncEnabled && isIngestion && !isWebhook && (
              <span className="ml-auto text-[11.5px] text-muted-foreground">
                Next sync {formatRelativeTime(source.nextSyncAt)}
              </span>
            )}
            {!isOff && !isIngestion && (
              <span className="ml-auto text-[11.5px] text-muted-foreground">
                Answers come straight from the source — Zamino stores nothing
              </span>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

const PanelStat: React.FC<{ label: string; value: string; tone?: 'success' | 'info' | 'muted' }> = ({
  label,
  value,
  tone,
}) => (
  <div className="rounded-lg border border-border bg-card px-2.5 py-2">
    <div className="text-[9.5px] font-bold uppercase tracking-wider text-muted-foreground">{label}</div>
    <div
      className={clsx(
        'text-[15px] font-semibold mt-0.5 tabular-nums',
        tone === 'success' && 'text-success',
        tone === 'info' && 'text-info',
        tone === 'muted' && 'text-muted-foreground',
        !tone && 'text-foreground',
      )}
    >
      {value}
    </div>
  </div>
);

const QuickLink: React.FC<{
  icon: React.ReactNode;
  children: React.ReactNode;
  onClick?: () => void;
  disabled?: boolean;
  title?: string;
}> = ({ icon, children, onClick, disabled, title }) => (
  <button
    type="button"
    disabled={disabled}
    title={title}
    onClick={onClick}
    className={clsx(
      'inline-flex items-center gap-1.5 rounded-[10px] border border-input bg-card px-2.5 py-1.5 text-[12.5px] font-medium text-foreground',
      disabled ? 'opacity-50 cursor-not-allowed' : 'hover:bg-accent',
    )}
  >
    {icon}
    {children}
  </button>
);

export default SourceRow;
